import React from 'react';
import { useTheme } from '@mui/material/styles';

// design
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import './ParentConfig.css';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import FormControl from '@mui/material/FormControl';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import FormLabel from '@mui/material/FormLabel';
import IconButton from '@mui/material/IconButton';
import SettingsIcon from '@mui/icons-material/Settings';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

// react-select common wrapper
import CommonSelect, { Option } from '../../common/commonSelect/CommonSelect';

 // マスタ値
import { OptionsBase, OptionsSelectList, useDivisionOptions, useTeamOptions, useTeamClassifications } from '../../../state/master'
import { DIALOG_KEYS } from '../../../constants';

interface ConfigProps {
  division: number;
  setDivision: (value: number) => void;
  team: number;
  setTeam: (value: number) => void;
  openSettingDialog: (
    title: string,
    meta: OptionsBase,
    rows: OptionsSelectList[],
    division?: boolean,
    freeOption?: boolean
  ) => void;
  // /get_team_docset_list から取得した enlight_team を親から受け取って表示に使用
  enlightTeam?: string | null;
  enlightSubteam?: string | null;
  enlightTeamLoading?: boolean;
  // /get_team_docset_list の生レスポンス（author_system, wr_app_team 判定用）
  teamDocsetData?: any | null;
  openEnlightTeamSettingDialog: () => void;
  openTeamClassificationDialog: () => void;
  openEnlightIDConnectionCopyDialog: () => void;
}

const ParentConfig: React.FC<ConfigProps> = ({
  division, setDivision, team, setTeam, openSettingDialog, enlightTeam, enlightSubteam, enlightTeamLoading, teamDocsetData, openEnlightTeamSettingDialog, openTeamClassificationDialog, openEnlightIDConnectionCopyDialog,
}) => {
  const muiTheme = useTheme();
  const isDark = muiTheme.palette.mode === 'dark';
  // マスタ値
  const { divisionOptions, divisionOptionsRaw } = useDivisionOptions();
  const { teamOptions, teamOptionsRaw } = useTeamOptions();
 
  const filterTeamOptions = teamOptions.filter((state) => state.department_id === division);
  const filterTeamOptionsRaw = teamOptionsRaw.filter((state) => state.department_id === division);

  // EnlightTeam 表示用（親から取得結果を受け取る）
  const normalizeEnlightTeam = (v: any): string | null => {
    const s = String(v ?? '').trim();
    if (!s) return null;
    if (s.toLowerCase() === 'none' || s.toLowerCase() === 'null') return null;
    return s;
  };
  const wrAppTeamDisplay = React.useMemo(() => {
    const normalize = (v: any): string => String(v ?? '').trim();
    const isWrInputAuthor = (v: any): boolean => {
      const s = normalize(v).toLowerCase();
      return s === 'wr_input_app' || s === 'weekly-report-input-app';
    };

    const arr = Array.isArray(teamDocsetData)
      ? teamDocsetData
      : Array.isArray((teamDocsetData as any)?.list)
        ? (teamDocsetData as any).list
        : Array.isArray((teamDocsetData as any)?.data)
          ? (teamDocsetData as any).data
          : teamDocsetData
            ? [teamDocsetData]
            : [];

    if (!Array.isArray(arr) || arr.length === 0) return null;
    const hit = arr.find((x: any) => isWrInputAuthor((x as any)?.author_system));
    if (!hit) return null;
    const wrAppTeam = normalize((hit as any)?.wr_app_team);
    return wrAppTeam || null;
  }, [teamDocsetData]);
  const enlightDisplay = (
    enlightTeamLoading
      ? '読込中...'
      : (() => {
        const t = normalizeEnlightTeam(enlightTeam);
        if (!t) return wrAppTeamDisplay ? `未連携（${wrAppTeamDisplay}）` : '未連携';
        const s = normalizeEnlightTeam(enlightSubteam);
        return s ? `${t} ${s}` : t;
      })()
  );
  const { teamClassifications } = useTeamClassifications();
  const teamClassNameMap = React.useMemo(() => {
    const map: Record<number, string> = {};
    (teamClassifications ?? []).forEach((row: any) => {
      const id = Number((row as any)?.class_id ?? (row as any)?.id);
      const name = String((row as any)?.class_name ?? (row as any)?.classification_name ?? (row as any)?.name ?? '').trim();
      if (!Number.isFinite(id) || !name) return;
      map[id] = name;
    });
    return map;
  }, [teamClassifications]);

  const currentTeamClassName = React.useMemo(() => {
    const foundTeam = (teamOptions ?? []).find(
      (t: any) => Number((t as any)?.team_id ?? (t as any)?.id ?? (t as any)?.value) === Number(team)
    );
    const classId = Number((foundTeam as any)?.class_id ?? (foundTeam as any)?.classId);
    if (!Number.isFinite(classId) || classId <= 0) return '';
    return String(teamClassNameMap[classId] ?? '').trim();
  }, [teamOptions, team, teamClassNameMap]);

  // meta値
  const divisionMeta = {
      attr_id: 0,
      label: '課',
      department_id: 0,
      attr_type: 11,
      required: true,
      multiple: false,
      status: true,
      options: divisionOptionsRaw
  };
  const teamMeta = {
      attr_id: 0,
      label: 'チーム',
      department_id: 0,
      attr_type: 11,
      required: true,
      multiple: false,
      status: true,
      options: filterTeamOptionsRaw
  };

  // react-select options and handlers
  const divisionSelectOptions: Option[] = divisionOptions.map((row: any) => ({
    value: String(row?.department_id ?? row?.value ?? ''),
    label: String(row?.label ?? ''),
  }));
  const teamSelectOptions: Option[] = filterTeamOptions.map((row: any) => ({
    value: String(row?.team_id ?? row?.value ?? ''),
    label: String(row?.label ?? ''),
  }));
 
  const selectedDivision: Option | null =
    division ? (divisionSelectOptions.find((o) => Number(o.value) === Number(division)) ?? null) : null;
  const selectedTeam: Option | null =
    team ? (teamSelectOptions.find((o) => Number(o.value) === Number(team)) ?? null) : null;
 
  const divisionChange = (opt: Option | null) => {
    const nextDivision = Number(opt?.value ?? 0);
    setDivision(nextDivision);
 
    // division 切替時はチームの先頭を自動選択
    const nextTeamOptions = teamOptions.filter((state) => state.department_id === nextDivision);
    if (nextTeamOptions.length > 0) {
      const firstTeamId = Number((nextTeamOptions[0] as any).team_id ?? 0);
      setTeam(firstTeamId);
    } else {
      // 対応するチームが無い場合は未選択状態（0）にする
      setTeam(0 as any);
    }
  };
  const teamChange = (opt: Option | null) => {
    setTeam(Number(opt?.value ?? 0));
  };
 
  const handleEnlightClick = (e: React.MouseEvent) => {
    e.preventDefault();
    openEnlightTeamSettingDialog();
  };

  const handleEnlightKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openEnlightTeamSettingDialog();
    }
  };

  const handleTeamClassClick = (e: React.MouseEvent) => {
    e.preventDefault();
    openTeamClassificationDialog();
  };

  const handleTeamClassKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openTeamClassificationDialog();
    }
  };

  const handleEnlightCopyIconClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    openEnlightIDConnectionCopyDialog();
  };


  return (
    <>
      <Card sx={{ backgroundColor: isDark ? '#0b1120' : '#f0f0f0' }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex', alignItems: 'center' }}>
              <Grid container spacing={2} sx={{ width: '100%' }}>
                <Grid size={{ xs: 12, sm: 6 }} className="search-item date-item" display="flex" justifyContent="center" alignItems="center">
                  <FormControl fullWidth>
                    <FormLabel id="division-label" sx={{ mb: 0.5, fontSize: '0.75rem' }}>設定を行う課を選択</FormLabel>
                    <CommonSelect
                      id="division"
                      options={divisionSelectOptions}
                      value={selectedDivision}
                      onChange={divisionChange}
                      placeholder="課を入力して絞り込み..."
                      isClearable={false}
                      isSearchable={true}
                      allowCreate={false}
                      className="parentconfig-select"
                    />
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }} className="search-item date-item" display="flex" justifyContent="center" alignItems="center">
                  <FormControl fullWidth>
                    <FormLabel id="team-label" sx={{ mb: 0.5, fontSize: '0.75rem' }}>チームを選択</FormLabel>
                    <CommonSelect
                      id="team"
                      options={teamSelectOptions}
                      value={selectedTeam}
                      onChange={teamChange}
                      placeholder="チームを入力して絞り込み..."
                      isClearable={false}
                      isSearchable={true}
                      allowCreate={false}
                      className="parentconfig-select"
                    />
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                  gap: 2,
                  width: '100%',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Button size="large" variant="contained" color="success" onClick={() => openSettingDialog(DIALOG_KEYS.DIVISION, divisionMeta, divisionOptionsRaw, true, false)}>課を修正する</Button>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Button size="large" variant="contained" color="success" onClick={() => openSettingDialog(DIALOG_KEYS.TEAM, teamMeta, filterTeamOptionsRaw, true, false)}>チームを修正する</Button>
                </Box>
                <FormControl fullWidth sx={{ width: '75%', justifySelf: 'center' }}>
                  <FormLabel id="enlight-id-label" sx={{ mb: 0.5, fontSize: '0.75rem', fontWeight: 'bold' }}>Enlight ID連携</FormLabel>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <div
                      id="enlight-id-link"
                      role="group"
                      aria-labelledby="enlight-id-label"
                      onClick={handleEnlightClick}
                      onKeyDown={handleEnlightKeyDown}
                      tabIndex={0}
                      data-department-id={division}
                      data-team-id={team}
                      style={{ backgroundColor: isDark ? '#1e293b' : 'white', border: `1px solid ${isDark ? '#334155' : 'rgba(0,0,0,0.23)'}`, borderRadius: 4, minHeight: 38, padding: '0 8px', display: 'flex', alignItems: 'center', cursor: 'pointer', width: '100%' }}
                    >
                      {enlightTeamLoading ? '読込中...' : (
                        <Link
                          component="span"
                          underline="hover"
                          sx={{ flex: '1 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                          {enlightDisplay}
                        </Link>
                      )}
                      <IconButton
                        aria-label="Enlight IDを設定"
                        size="small"
                        sx={{ m: 0 }}
                        style={{ margin: 0 }}
                      >
                        <SettingsIcon fontSize="small" />
                      </IconButton>
                    </div>
                    <IconButton
                      aria-label="Enlight ID連携情報をコピー"
                      size="small"
                      sx={{ m: 0, flex: '0 0 auto' }}
                      style={{ margin: 0 }}
                      onClick={handleEnlightCopyIconClick}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </FormControl>
                <FormControl fullWidth sx={{ width: '75%', justifySelf: 'center' }}>
                  <FormLabel id="team-class-label" sx={{ mb: 0.5, fontSize: '0.75rem', fontWeight: 'bold' }}>チーム分類</FormLabel>
                  <div
                    role="group"
                    aria-labelledby="team-class-label"
                    onClick={handleTeamClassClick}
                    onKeyDown={handleTeamClassKeyDown}
                    tabIndex={0}
                    data-department-id={division}
                    data-team-id={team}
                    style={{ backgroundColor: isDark ? '#1e293b' : 'white', border: `1px solid ${isDark ? '#334155' : 'rgba(0,0,0,0.23)'}`, borderRadius: 4, minHeight: 38, padding: '0 8px', display: 'flex', alignItems: 'center', cursor: 'pointer', width: '100%' }}
                  >
                    <Link
                      component="span"
                      underline="hover"
                      sx={{ flex: '1 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {currentTeamClassName || '分類なし'}
                    </Link>
                    <IconButton
                      aria-label="チーム分類を設定"
                      size="small"
                      sx={{ m: 0 }}
                      style={{ margin: 0 }}
                    >
                      <SettingsIcon fontSize="small" />
                    </IconButton>
                  </div>
                </FormControl>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  );
};

export default ParentConfig;
